/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, TextField, Button, ErrorText } from '@components/atoms'

import {
	useAppSelector,
	useAppDispatch,
	paymentActions,
	CartType,
	PaymentShippingType,
	PaymentCostType,
	PaymentDeliveryType,
} from '@redux/index'
import { SeasonProductType } from '@schemas'
import { Decimal } from '@utils'
import { produce } from 'immer'
import { grey, pink } from '@mui/material/colors'

export const PaymentTotalCost = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const paymentMethod: string = useAppSelector(state => state.payment.paymentMethod)
	const cartList: CartType[] = useAppSelector(state => state.payment.tryToPayment)[
		'cartList'
	]
	const shipping: PaymentShippingType = useAppSelector(
		state => state.payment.paymentShipping,
	)
	const cost: PaymentCostType = useAppSelector(state => state.payment.paymentCost)
	const errorMessage: string = useAppSelector(
		state => state.payment.paymentCouponErrorMessage,
	)
	const delivery: PaymentDeliveryType = useAppSelector(
		state => state.payment.paymentDelivery,
	)
	const seasonProductList: SeasonProductType[] = useAppSelector(
		state => state.shop.productList,
	)

	// state
	const [krwSubtotal, setKrwSubtotal] = React.useState<number>(0)

	React.useEffect(() => {
		getKrwSubtotal()
	}, [])

	React.useEffect(() => {
		getKrwSubtotal()
	}, [cartList, seasonProductList, paymentMethod, delivery, cost])

	const getKrwSubtotal = () => {
		if (delivery.countryIsoCode !== 'KR') {
			setKrwSubtotal(0)
		} else {
			const res = cartList.reduce((sum: number, anItem: CartType) => {
				const idx = seasonProductList.findIndex(
					anItem2 => anItem2.productCode === anItem.productCode,
				)
				console.log(anItem)
				if (idx > -1) {
					sum += anItem.qty * seasonProductList[idx].seasonKrw
				}

				return sum - cost.discountedKrw
			}, 0)

			setKrwSubtotal(res)
		}
	}

	const totalQty = cartList.reduce((sumQty, aCart) => sumQty + aCart.qty, 0)

	const totalCost = React.useMemo(() => {
		const subtotal: {
			sumUsd: number
			vatUsd: number
			qty: number
		} = cartList.reduce(
			(sum, { sumUsd, vatUsd, qty }) => {
				return {
					sumUsd: Decimal(sum.sumUsd + sumUsd),
					vatUsd: Decimal(sum.vatUsd + vatUsd),
					qty: sum.qty + qty,
				}
			},
			{
				sumUsd: 0,
				vatUsd: 0,
				qty: 0,
			},
		)

		const shippingUsd = shipping.usd
		const newPaidUsd =
			cost.discountedUsd === 0
				? Decimal(subtotal.sumUsd + shippingUsd)
				: Decimal(subtotal.sumUsd + shippingUsd - cost.discountedUsd)
		const vatRate = 10 / 110
		const newVat = Decimal(newPaidUsd * vatRate)
		const newSubtotal = Decimal(newPaidUsd - newVat)

		if (
			cost.totalUsd !== newSubtotal ||
			cost.vatUsd !== newVat ||
			cost.shippingUsd !== shippingUsd ||
			cost.discountedUsd !== cost.discountedUsd ||
			cost.paidUsd !== newPaidUsd
		) {
			const newCost = produce(cost, draft => {
				draft.totalUsd = newSubtotal
				draft.vatUsd = newVat
				draft.shippingUsd = shippingUsd
				draft.discountedUsd = cost.discountedUsd
				draft.paidUsd = newPaidUsd
			})

			if (!isNaN(shippingUsd)) {
				reduxDispatch(
					paymentActions.PAYMENT_COST({
						...newCost,
					}),
				)
			}
		}

		return newPaidUsd
	}, [cartList, shipping, cost.discountedUsd])

	const onCouponChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const newCost = produce(cost, draft => {
			draft.couponCode = str
		})

		reduxDispatch(
			paymentActions.PAYMENT_COST({
				...newCost,
				discountedUsd: 0,
			}),
		)
	}

	const onCouponApplyClick = () => {
		if (cost.couponCode !== '') {
			reduxDispatch(paymentActions.TRIGGER_COUPON_APPLY(true))
		}
	}

	return (
		<View css={style}>
			<View className="infoTitleView">
				<Text category="h3">Total Cost</Text>
			</View>

			<View className="infoInnerView">
				<View className="totalItemView">
					<Text category="s1" className="subtotalLabel">
						{`Subtotal x ${totalQty} Items :`}
					</Text>
					<Text category="h4" className="subtotalValue">
						{delivery.countryIsoCode === 'KR'
							? krwSubtotal.toLocaleString('ko-KR', {
									style: 'currency',
									currency: 'KRW',
							  })
							: (totalCost + cost.discountedUsd - shipping.usd).toLocaleString('en-US', {
									style: 'currency',
									currency: 'USD',
							  })}
					</Text>
				</View>
				<View className="totalItemView">
					<Text category="s1" className="subtotalLabel">
						Shipping
					</Text>
					<Text category="h4" className="subtotalValue">
						{(Number(shipping.usd) || 0).toLocaleString('en-US', {
							style: 'currency',
							currency: delivery.countryIsoCode === 'KR' ? 'KRW' : 'USD',
						})}
					</Text>
				</View>

				<View className="couponView">
					<Text category="s1" className="subtotalLabel">
						Coupon Code
					</Text>
					<View className="couponInputView">
						<View className="couponInputInnerView">
							<TextField
								type="text"
								label="Coupon Code"
								error={errorMessage !== ''}
								value={cost.couponCode}
								onChange={onCouponChange}
							/>

							<Button
								variant="contained"
								className="couponApplyButton"
								disabled={cost.couponCode === ''}
								onClick={onCouponApplyClick}
							>
								Apply
							</Button>
						</View>

						{errorMessage !== '' && (
							<ErrorText className="errorMsg">{errorMessage}</ErrorText>
						)}
					</View>
				</View>

				{cost.discountedUsd > 0 && (
					<View className="totalItemView">
						<Text category="s1" className="subtotalLabel">
							Coupon Discounted
						</Text>
						<Text category="h4" className="subtotalValue discount">
							{'- ' +
								cost.discountedUsd.toLocaleString('en-US', {
									style: 'currency',
									currency: 'USD',
								})}
						</Text>
					</View>
				)}

				<View className="totalItemView total">
					<Text category="h2" className="subtotalLabel">
						Total
					</Text>
					<Text category="h1" className="subtotalValue">
						{delivery.countryIsoCode === 'KR'
							? krwSubtotal.toLocaleString('ko-KR', {
									style: 'currency',
									currency: 'KRW',
							  })
							: totalCost.toLocaleString('en-US', {
									style: 'currency',
									currency: 'USD',
							  })}
					</Text>
				</View>
				{/* <View className="vatInfoView">
					<Text category="p1" hint="500" className="vatLabel">
						(VAT Included)
					</Text>
				</View> */}
			</View>
		</View>
	)
}

const style = css({
	marginBottom: '2rem',
	paddingLeft: '1rem',

	'.infoTitleView': {
		textAlign: 'left',
	},

	'.totalItemView': {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginTop: '1rem',
		borderBottom: '1px solid',
		borderColor: grey[300],
		textAlign: 'left',

		'.subtotalLabel': {
			marginRight: '1rem',
			textAlign: 'left',
			marginTop: 0,
		},

		'.subtotalValue': {
			marginTop: 0,

			'&.discount': {
				color: pink['A400'],
			},
		},

		'&.total': {
			justifyContent: 'flex-end',
			borderBottom: 0,

			'.subtotalLabel': {
				marginBottom: 0,
			},
			'.subtotalValue': {
				marginBottom: 0,
			},
		},
	},

	'.couponView': {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		marginTop: '1rem',
		textAlign: 'left',

		'.subtotalLabel': {
			width: '20rem',
		},

		'.couponInputView': {
			width: '100%',

			'.couponInputInnerView': {
				display: 'flex',
				alignItems: 'center',
				'.couponApplyButton': {
					width: '8rem',
					height: '3.5rem',
					marginLeft: '0.5rem',
				},
			},

			'.errorMsg': {
				textAlign: 'left',
			},
		},
	},

	'.vatInfoView': {
		marginTop: 0,
		textAlign: 'right',
	},

	'@media (max-width: 750px)': {
		'.couponView': {
			display: 'block',
			'.subtotalLabel': {
				width: '100%',
			},
		},
	},
})
